import { WFStoGeoJson } from "src/functions/WFSutils";
import { fitGmlPreview, orthophotoTilePlacements } from "src/managers/GmlPreviewFrame";
import Denmark from "src/mapStyles/sources/Denmark.json";

const WIDTH = 1000;
const HEIGHT = 640;
const FILL = "rgba(198, 40, 40, 0.28)";
const STROKE = "#c62828";

let orthophotoTemplate: Promise<string | null> | null = null;

function orthophotoTileTemplate(): Promise<string | null> {
  if (!orthophotoTemplate) {
    const sourceUrl = Denmark.orthophoto?.url;
    orthophotoTemplate = sourceUrl
      ? fetch(sourceUrl)
        .then((response) => response.json())
        .then((tileJson: { tiles?: string[] }) => tileJson.tiles?.[0] || null)
        .catch(() => null)
      : Promise.resolve(null);
  }
  return orthophotoTemplate;
}

async function drawOrthophoto(
  ctx: CanvasRenderingContext2D,
  bounds: { minX: number; minY: number; maxX: number; maxY: number },
): Promise<boolean> {
  const template = await orthophotoTileTemplate();
  if (!template) {
    return false;
  }
  const placements = orthophotoTilePlacements(bounds, WIDTH, HEIGHT);
  const images = await Promise.all(placements.map(async (tile) => {
    const url = template
      .replace("{z}", `${tile.z}`)
      .replace("{x}", `${tile.x}`)
      .replace("{y}", `${tile.y}`);
    try {
      const response = await fetch(url);
      if (!response.ok) {
        return null;
      }
      return { tile, bitmap: await createImageBitmap(await response.blob()) };
    } catch {
      return null;
    }
  }));
  let drawn = false;
  images.forEach((image) => {
    if (!image) {
      return;
    }
    ctx.drawImage(image.bitmap, image.tile.left, image.tile.top, image.tile.width, image.tile.height);
    image.bitmap.close?.();
    drawn = true;
  });
  return drawn;
}

function collectCoords(value: unknown, into: Array<[number, number]>) {
  if (!Array.isArray(value) || !value.length) {
    return;
  }
  if (typeof value[0] === "number" && typeof value[1] === "number") {
    into.push([value[0], value[1]]);
    return;
  }
  value.forEach((child) => collectCoords(child, into));
}

function drawRing(
  ctx: CanvasRenderingContext2D,
  ring: number[][],
  project: (coord: number[]) => [number, number],
  close: boolean,
) {
  if (!ring.length) {
    return;
  }
  const [x, y] = project(ring[0]);
  ctx.beginPath();
  ctx.moveTo(x, y);
  for (let index = 1; index < ring.length; index += 1) {
    const [px, py] = project(ring[index]);
    ctx.lineTo(px, py);
  }
  if (close) {
    ctx.closePath();
  }
}

/** Off-screen picture of a GML drawing. Does not touch the open map. */
export async function renderGmlPreviewPng(gmlText: string): Promise<Uint8Array | null> {
  if (typeof document === "undefined" || !gmlText.trim()) {
    return null;
  }
  let layers: Array<{ geoJson?: { features?: Array<{ geometry?: { type?: string; coordinates?: unknown } }> } }> = [];
  try {
    layers = WFStoGeoJson(gmlText);
  } catch {
    return null;
  }
  const features = layers.flatMap((layer) => layer.geoJson?.features || []);
  const coords: Array<[number, number]> = [];
  features.forEach((feature) => collectCoords(feature.geometry?.coordinates, coords));
  if (!coords.length) {
    return null;
  }
  const { project, mercatorBounds } = fitGmlPreview(coords, WIDTH, HEIGHT);

  const canvas = document.createElement("canvas");
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return null;
  }
  ctx.fillStyle = "#d5ddd8";
  ctx.fillRect(0, 0, WIDTH, HEIGHT);
  if (mercatorBounds) {
    await drawOrthophoto(ctx, mercatorBounds);
  }
  ctx.lineJoin = "round";
  ctx.lineCap = "round";

  const paintRing = (ring: number[][], close: boolean, fill: boolean) => {
    drawRing(ctx, ring, project, close);
    ctx.strokeStyle = "rgba(255, 255, 255, 0.95)";
    ctx.lineWidth = 7;
    ctx.stroke();
    if (fill) {
      ctx.fillStyle = FILL;
      ctx.fill();
    }
    drawRing(ctx, ring, project, close);
    ctx.strokeStyle = STROKE;
    ctx.lineWidth = 3;
    ctx.stroke();
  };

  for (const feature of features) {
    const geometry = feature.geometry;
    if (!geometry?.coordinates) {
      continue;
    }
    if (geometry.type === "Polygon") {
      (geometry.coordinates as number[][][]).forEach((ring, index) => paintRing(ring, true, index === 0));
    } else if (geometry.type === "MultiPolygon") {
      (geometry.coordinates as number[][][][]).forEach((polygon) => {
        polygon.forEach((ring, index) => paintRing(ring, true, index === 0));
      });
    } else if (geometry.type === "LineString") {
      paintRing(geometry.coordinates as number[][], false, false);
    } else if (geometry.type === "MultiLineString") {
      (geometry.coordinates as number[][][]).forEach((line) => paintRing(line, false, false));
    } else if (geometry.type === "Point") {
      const [x, y] = project(geometry.coordinates as number[]);
      ctx.beginPath();
      ctx.arc(x, y, 7, 0, Math.PI * 2);
      ctx.fillStyle = "#ffffff";
      ctx.fill();
      ctx.beginPath();
      ctx.arc(x, y, 5, 0, Math.PI * 2);
      ctx.fillStyle = STROKE;
      ctx.fill();
    }
  }

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
  if (!blob) {
    return null;
  }
  return new Uint8Array(await blob.arrayBuffer());
}
